/**
 * AccessDenied — shared 403 surface for route guards.
 *
 * Used by:
 *   - RequirePermissionRoute (variant: 'permission') — user lacks required perm
 *   - RequirePlatformAdminRoute (variant: 'platform') — user is not a platform admin
 *   - RequireTenantScopedRoute (variant: 'tenant') — platform admin in platform mode
 *     navigated directly to a tenant-self surface without selecting a tenant first
 *
 * Renders a safe, operator-friendly message. Does NOT expose backend details.
 * Does NOT redirect — the explicit 403 card makes the access boundary visible
 * to both the operator and support teams reviewing screenshots.
 */
import { Link } from 'react-router-dom'
import { Button } from '@/components/ui/Button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card'

interface AccessDeniedProps {
  /**
   * 'permission' — the user lacks a required permission for this surface.
   * 'platform'   — the surface is restricted to platform administrators only.
   * 'tenant'     — the surface requires a tenant context; platform admins must
   *                select a tenant via the tenant-view switcher first.
   * 'tenant-self' — a /tenants/self/* surface: the backend rejects every
   *                platform-scope session (even in tenant-view mode), so
   *                platform admins must use the platform equivalent.
   */
  variant: 'permission' | 'platform' | 'tenant' | 'tenant-self'
  /**
   * Optional escape hatch shown under the message (e.g. the platform
   * equivalent of a tenant-self page). Rendered as a real link.
   */
  action?: { label: string; to: string }
}

function resolveTitle(variant: AccessDeniedProps['variant']): string {
  if (variant === 'platform') return 'Platform administrators only'
  if (variant === 'tenant') return 'Tenant context required'
  if (variant === 'tenant-self') return 'Not available in a platform session'
  return 'Access denied'
}

function resolveMessage(variant: AccessDeniedProps['variant']): string {
  if (variant === 'platform') {
    return 'This page is restricted to platform administrators. If you believe you need access, contact your platform administrator.'
  }
  if (variant === 'tenant-self') {
    return 'This is a tenant self-service page and cannot be used from a platform administrator session, even when viewing as a tenant. Use the platform page and pick the tenant with the tenant selector.'
  }
  if (variant === 'tenant') {
    return 'This page is only accessible in tenant context. Use the tenant selector to switch into a specific tenant before navigating here, or log in as a tenant administrator.'
  }
  return "You don't have permission to view this page. If you think this is wrong, contact your team lead."
}

export function AccessDenied({ variant, action }: AccessDeniedProps) {
  return (
    <div className="flex min-h-[60vh] items-center justify-center p-6">
      <Card className="max-w-md">
        <CardHeader>
          <CardTitle>{resolveTitle(variant)}</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-fg">{resolveMessage(variant)}</p>
          {action ? (
            <Button asChild size="sm" className="mt-3">
              <Link to={action.to}>{action.label}</Link>
            </Button>
          ) : null}
        </CardContent>
      </Card>
    </div>
  )
}
